import { create } from "zustand";
import { jwtDecode } from "jwt-decode";
import { apiRequest } from "services/api";
import { getCookie, removeCookie, setCookie } from "services/cookies";
import { UserDataModel } from "./models";
import { NavigateFunction } from "react-router";
import { OrganizationModel } from "modules/organization/model";
import { OrganizationUnitModel } from "modules/organization-unit/model";

export interface UserStateModel {
  loading: boolean;
  isAuthenticated: boolean;
  data: UserDataModel | null;
  token: any | null;
  error: any | null;
}

interface UserStore extends UserStateModel {
  loginUser: (email: string, password: string, navigate: NavigateFunction) => Promise<void>;
  logUserOut: (navigate: NavigateFunction) => void;
  loadMyUser: (navigate: NavigateFunction) => Promise<void>;
  myUserRefreshData: () => Promise<void>;
}

export const useUserStore = create<UserStore>((set) => ({
  loading: false,
  isAuthenticated: false,
  data: null,
  token: null,
  error: null,

  loginUser: async (email, password, navigate) => {
    removeCookie();
    set({ loading: true });
    try {
      const data = await apiRequest({
        endpoint: "/token",
        method: "post",
        payload: { email, password },
        noAuth: true,
      });
      await setCookie(data.token);
      set({ loading: false, isAuthenticated: true, token: jwtDecode(data.token) });
      navigate("/");
    } catch {
      const { useNotificationStore } = await import("modules/notifications/store");
      useNotificationStore.getState().errorNotification("Login failed...");
      set({ loading: false });
    }
  },

  logUserOut: (navigate) => {
    removeCookie();
    set({ isAuthenticated: false, token: null, data: null });
    import("modules/app/store").then(({ useAppStore }) => {
      useAppStore.getState().clear();
    });
    navigate("/");
  },

  loadMyUser: async (navigate) => {
    const token = getCookie();
    if (token === "") {
      navigate("/login");
      return;
    }
    set({ loading: true });
    try {
      const userData = await apiRequest({
        endpoint: "/my/user",
        method: "get",
      });
      set({ data: userData, loading: false });

      const { organizations, organizationUnits } = userData;
      const { useAppStore } = await import("modules/app/store");
      const selectedOrganizationUuid = useAppStore.getState().selectedOrganizationUuid;
      const hasAccessToSelected = organizations.find(
        (o: OrganizationModel) => o.uuid === selectedOrganizationUuid
      );

      if (!hasAccessToSelected) {
        const firstOrganization = organizations[0];
        await useAppStore.getState().selectOrganization(firstOrganization.uuid);

        const unit = organizationUnits.find(
          (ou: OrganizationUnitModel) =>
            ou.organizationUuid === firstOrganization.uuid
        );
        if (unit) {
          await useAppStore.getState().selectOrganizationUnit(unit.uuid);
        }
      }
    } catch {
      navigate("/login");
    }
  },

  myUserRefreshData: async () => {
    try {
      const data = await apiRequest({
        endpoint: "/my/user",
        method: "get",
      });
      set({ data });
    } catch {
      // error silently
    }
  },
}));
